<script lang="ts" setup>
import type { IProps, IEmits } from './TeamModal.types'

const { id, modelValue, allBoardMembers } = defineProps<IProps>()

const emit = defineEmits<IEmits>()

const modalRef = ref<HTMLElement | null>(null)
const previousActiveElement = ref<HTMLElement | null>(null)

const trapFocus = (event: KeyboardEvent) => {
  if (!modalRef.value) return

  const FOCUSABLE_ELEMENTS_SELECTOR = 'button:not(:disabled), [href], input, [tabindex]:not([tabindex="-1"])'
  const elements = modalRef.value.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR)
  const firstElement = elements[0] as HTMLElement
  const lastElement = elements[elements.length - 1] as HTMLElement

  if (event.shiftKey) {
    if (document.activeElement === firstElement) {
      lastElement.focus()
      event.preventDefault()
    }
  } else if (document.activeElement === lastElement) {
    firstElement.focus()
    event.preventDefault()
  }
}

const isFirstMember = computed(() => modelValue === 0)
const isLastMember = computed(() => modelValue === allBoardMembers.length - 1)

const prevMember = () => {
  if (!isFirstMember.value) {
    emit('update:modelValue', modelValue - 1)
    nextTick(() => {
      modalRef.value?.focus()
    })
  }
}

const nextMember = () => {
  if (!isLastMember.value) {
    emit('update:modelValue', modelValue + 1)
    nextTick(() => {
      modalRef.value?.focus()
    })
  }
}

const activeMember = computed(() => allBoardMembers[modelValue])

const onClickCloseModal = () => {
  emit('update:modelValue', -1)
}

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowLeft') {
    prevMember()
  } else if (event.key === 'ArrowRight') {
    nextMember()
  } else if (event.key === 'Escape') {
    onClickCloseModal()
  } else if (event.key === 'Tab') {
    trapFocus(event)
  }
}

onMounted(() => {
  previousActiveElement.value = document.activeElement as HTMLElement
  nextTick(() => {
    modalRef.value?.focus()
  })
  window.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
  previousActiveElement.value?.focus()
})

const mapLine = () => {
  return activeMember.value.boardMemberDescription.content.map(el => el.content[0].value)
}
</script>

<template>
  <Teleport
    to="body"
    :disabled="teleportDisabled"
  >
    <div
      ref="modalRef"
      class="Team-Modal"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="id"
      tabindex="-1"
      @click.self="onClickCloseModal"
    >
      <div class="Team-Modal__container">
        <button
          class="Team-Modal__closeButton"
          aria-label="Zamknij modal"
          @click="onClickCloseModal"
        >
          <img src="/icons/icn_close.svg">
        </button>

        <div class="Team-Modal__wrapper">
          <div class="Team-Modal__memberContainer">
            <div class="Team-Modal__header">
              <div class="Team-Modal__headerLeftColumn">
                <img
                  v-if="activeMember.boardMemberPhoto"
                  class="Team-Modal__photo"
                  :src="activeMember.boardMemberPhoto"
                  alt=""
                >
              </div>

              <div class="Team-Modal__headerRightColumn">
                <p
                  :id="id"
                  class="Team-Modal__name"
                >
                  {{ activeMember.boardMemberName }}
                </p>

                <p class="Team-Modal__position">
                  {{ activeMember.boardMemberRole }}
                </p>

                <a
                  v-if="activeMember.linkedInLink"
                  :href="activeMember.linkedInLink"
                  target="_blank"
                  class="Team-Modal__linkedin"
                >
                  Mój profil LinkedIn
                </a>
              </div>
            </div>

            <div class="Team-Modal__content">
              <p
                v-for="line in mapLine()"
                :key="line"
                class="Team-Modal__text"
              >
                {{ line }}
              </p>
            </div>
          </div>

          <div class="Team-Modal__arrowsContainer">
            <button
              class="Team-Modal__arrowButton"
              :disabled="isFirstMember"
              aria-label="Poprzedni profil"
              @click="prevMember"
            >
              <div class="Team-Modal__arrow Team-Modal__arrow--rotated">
                <img src="/icons/icn_arrow_right.svg">
              </div>

              <p class="Team-Modal__arrowDescription">
                {{ $t('common.prev') }}
              </p>
            </button>

            <button
              class="Team-Modal__arrowButton"
              :disabled="isLastMember"
              aria-label="Następny profil"
              @click="nextMember"
            >
              <p class="Team-Modal__arrowDescription">
                {{ $t('common.next') }}
              </p>

              <div class="Team-Modal__arrow">
                <img src="/icons/icn_arrow_right.svg">
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.Team-Modal {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background-color: $background-grey;

  &__container {
    position: relative;
    display: flex;
    flex-direction: column;
    max-width: 101.2rem;
    width: 100%;
    height: 100%;
    padding: 2.4rem;
    overflow: auto;
    background-color: $gray-100;
  }

  &__closeButton {
    @include h8;
    position: absolute;
    top: 2.4rem;
    right: 2.4rem;
    z-index: 1;
    color: $text-400;
    transition: outline 0.2s ease;

    &:focus {
      outline: none;
    }

    &:focus-visible {
      border-radius: 0.5rem;
      outline: 0.2rem solid $purple-400;
      outline-offset: 0.5rem;
    }
  }

  &__wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
  }

  &__memberContainer {
    display: flex;
    flex-direction: column;
  }

  &__header {
    margin-bottom: 2.4rem;
  }

  &__photo {
    width: 18rem;
    height: 20rem;
    margin-bottom: 2.4rem;
    border-radius: 1.6rem;
  }

  &__name {
    @include h7;
    margin-bottom: 0.8rem;
    color: $text-400;
  }

  &__position {
    @include h10;
    color: $text-400;
  }

  &__linkedin {
    @include body-1;
    width: fit-content;
    margin: 1rem 0 0 0;
    color: $text-300;
    border-bottom: 1px solid $text-300;
    transition: outline 0.2s ease;

    &:focus {
      outline: none;
    }

    &:focus-visible {
      border-radius: 0.5rem;
      outline: 0.2rem solid $purple-400;
      outline-offset: 0.5rem;
    }
  }

  &__text {
    @include body-2;
    margin-bottom: 2.4rem;
    color: $text-400;
  }

  &__arrowsContainer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  &__arrowButton {
    display: flex;
    align-items: center;
    gap: 1.2rem;
    border-radius: 8px;
    transition: outline 0.2s ease;

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid $purple-400;
      outline-offset: 4px;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;

      .Team-Modal__arrow {
        border-color: $gray-400;
      }

      .Team-Modal__arrowDescription {
        color: $text-400;
      }
    }
  }

  &__arrow {
    @include body-2-medium;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 3.2rem;
    height: 3.2rem;
    color: $text-400;
    border: 1px solid $gray-400;
    border-radius: 50%;

    &--rotated {
      transform: rotate(180deg);
    }
  }

  &__arrowDescription {
    @include body-2-medium;
    color: $text-400;
  }
}

@include atS {
  .Team-Modal {
    &__container {
      max-height: 88rem;
      height: unset;
      padding: 8.8rem 8rem 6.4rem 6.4rem;
      overflow: hidden;
      border-radius: 2.4rem;
    }

    &__wrapper {
      min-height: 100%;
    }

    &__closeButton {
      cursor: pointer;
    }

    &__header {
      display: flex;
      margin-bottom: 3.2rem;
    }

    &__headerLeftColumn {
      margin-right: 2.4rem;
    }

    &__photo {
      margin-bottom: 0;
    }

    &__headerRightColumn {
      display: flex;
      flex-direction: column;
      justify-content: center;
      max-width: 75%;
    }

    &__name {
      @include h4;
      margin-bottom: 1.6rem;
    }

    &__position {
      @include h7;
    }

    &__text {
      @include body-1;
      margin-bottom: 3.2rem;
    }

    &__arrowButton {
      cursor: pointer;

      &:disabled {
        cursor: not-allowed;
      }
    }

    &__content {
      max-height: 44rem;
      overflow: auto;
    }
  }
}
</style>
